import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Globe, Briefcase, Languages, User, Award } from "lucide-react";
import { usePersonalInfo } from "@/hooks/usePortfolio";
import { SectionTitle } from "@/components/SectionTitle";

export const PersonalInfo = () => {
  const { data: p, isLoading, error } = usePersonalInfo();

  if (isLoading) return <div>Loading...</div>;
  if (error || !p) return null;

  const initial = p.fullName ? p.fullName.charAt(0) : "M";

  return (
    <section id="personal-info" className="section-pad relative" data-testid="personal-info-section">
      <div className="container-x">
        <SectionTitle eyebrow="Personal" title={<>Get to know <span className="italic gold-text">the professional</span></>} subtitle="A bespoke profile — drawn out the way a leader is meant to be introduced." />

        <div className="relative max-w-6xl mx-auto">
          {/* Asymmetric bento layout */}
          <div className="grid grid-cols-12 gap-4 md:gap-5 auto-rows-[minmax(120px,auto)]">
            {/* Big monogram + name */}
            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
              className="col-span-12 md:col-span-7 row-span-2 glass rounded-3xl grain p-8 md:p-10 relative overflow-hidden"
            >
              <div className="absolute -top-12 -right-12 w-64 h-64 rounded-full" style={{ background: "radial-gradient(closest-side, hsl(var(--gold)/0.2), transparent)" }} />
              <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--gold-deep))] dark:text-[hsl(var(--gold-light))]">Curriculum Vitae</div>
              <div className="mt-3 flex items-baseline gap-3">
                <span className="font-serif text-7xl md:text-8xl gold-text leading-none">{initial}</span>
                <div className="font-serif text-2xl md:text-3xl leading-tight">
                  {p.fullName}
                  <div className="mt-1 text-sm font-sans text-muted-foreground tracking-wide">{p.designation}</div>
                </div>
              </div>
              <div className="hairline my-6" />
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                <div className="flex items-center gap-3 text-foreground/85" data-testid="pi-nationality">
                  <Globe className="w-4 h-4 text-[hsl(var(--gold-deep))] dark:text-[hsl(var(--gold-light))]" />
                  <span><span className="text-muted-foreground">Nationality · </span>{p.nationality || 'Not specified'}</span>
                </div>
                <div className="flex items-center gap-3 text-foreground/85" data-testid="pi-languages">
                  <Languages className="w-4 h-4 text-[hsl(var(--gold-deep))] dark:text-[hsl(var(--gold-light))]" />
                  <span><span className="text-muted-foreground">Languages · </span>{p.languages || 'English'}</span>
                </div>
              </div>
            </motion.div>

            {/* Experience badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.08 }}
              className="col-span-12 sm:col-span-6 md:col-span-5 glass rounded-3xl grain p-7 flex flex-col justify-between"
              data-testid="pi-experience"
            >
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Experience</div>
                <Award className="w-4 h-4 text-[hsl(var(--gold))]" />
              </div>
              <div>
                <div className="font-serif text-5xl md:text-6xl gold-text leading-none">{p.experience || '22+ Years'}</div>
                <div className="text-sm text-muted-foreground mt-2">In Sales, Marketing & Business Development</div>
              </div>
            </motion.div>

            {/* Email - wide */}
            <motion.a
              href={`mailto:${p.email}`}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.12 }}
              className="col-span-12 sm:col-span-6 md:col-span-5 glass rounded-3xl grain p-7 flex items-center gap-5 hover:border-[hsl(var(--gold))]/50 transition-colors"
              data-testid="pi-email"
            >
              <div className="w-12 h-12 rounded-2xl bg-[hsl(var(--gold))]/12 border border-[hsl(var(--gold))]/30 flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5 text-[hsl(var(--gold-deep))] dark:text-[hsl(var(--gold-light))]" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Email</div>
                <div className="text-base font-medium truncate mt-0.5">{p.email}</div>
              </div>
            </motion.a>

            {/* Phone */}
            <motion.a
              href={`tel:${(p.phone || '').replace(/\s/g, "")}`}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.16 }}
              className="col-span-12 sm:col-span-6 md:col-span-3 glass rounded-3xl grain p-7 hover:border-[hsl(var(--gold))]/50 transition-colors"
              data-testid="pi-phone"
            >
              <Phone className="w-5 h-5 text-[hsl(var(--gold-deep))] dark:text-[hsl(var(--gold-light))]" />
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-4">Phone</div>
              <div className="text-base font-medium mt-0.5">{p.phone}</div>
            </motion.a>

            {/* Specialisation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.20 }}
              className="col-span-12 sm:col-span-6 md:col-span-3 glass rounded-3xl grain p-7 relative overflow-hidden flex flex-col justify-center"
              data-testid="pi-specialisation"
            >
              <Briefcase className="w-5 h-5 text-[hsl(var(--gold-deep))] dark:text-[hsl(var(--gold-light))] mb-2" />
              <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Specialised in</div>
              <div className="font-serif text-lg mt-1 leading-tight">Telecom · Cloud · UCaaS</div>
            </motion.div>

            {/* Address - full width */}
            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.24 }}
              className="col-span-12 md:col-span-6 glass rounded-3xl grain p-7 flex items-start gap-5"
              data-testid="pi-address"
            >
              <div className="w-12 h-12 rounded-2xl bg-[hsl(var(--gold))]/12 border border-[hsl(var(--gold))]/30 flex items-center justify-center shrink-0">
                <MapPin className="w-5 h-5 text-[hsl(var(--gold-deep))] dark:text-[hsl(var(--gold-light))]" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Address</div>
                <div className="text-base font-medium mt-1 leading-relaxed">{p.address}, {p.city}, {p.state}, {p.country}</div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
